// 모듈 컨텐츠 영역 — 경로에 해당하는 모듈 번들을 런타임에 로드해 마운트 (구 ModuleContent 승계)
// keep-alive: 한 번 방문한 모듈은 언마운트하지 않고 display:none으로 숨겨 상태를 유지한다.
import React, { useEffect, useRef, useState } from 'react';

// 모듈 경로 접두사 — 항상 동일 origin(상대경로). 모듈 번들은 호스트와 같은 docroot의
// /modules/<code>에 배포되므로(로컬은 setupProxy, 배포는 nginx) 절대 URL을 쓸 이유가 없다.
// 게이트웨이 API(절대 URL 고정 정책)와 달리 이건 정적 번들이라 CORS에 막힌다.
const BASE = '';

interface ModuleApi {
  mount: (el: HTMLElement) => void;
  unmount: (el: HTMLElement) => void;
}

interface ModuleDef {
  script?: string;
  /** script 방식 모듈이 함께 배출하는 스타일시트 — 번들에 인라인되지 않으므로 호스트가 붙인다 */
  styles?: string[];
  manifest?: string;
  getApi: () => ModuleApi | undefined;
}

// moduleCode → 로드 정보 매핑 (모듈 추가 시 여기에 등록)
// - script: 단일 번들(Vite lib 모드)을 바로 로드
// - manifest: CRA(webpack4)의 asset-manifest.json entrypoints를 순서대로 로드
const MODULES: Record<string, ModuleDef> = {
  'yukimushi-base-vite-fe': {
    script: `${BASE}/modules/yukimushi-base-vite-fe/module.js`,
    getApi: () => (window as any).YukimushiBaseViteFe,
  },
  'yukimushi-base-webpack-fe': {
    manifest: `${BASE}/modules/yukimushi-base-webpack-fe`,
    getApi: () => (window as any).YukimushiBaseWebpackFe,
  },
  'yukimushi-board-fe': {
    manifest: `${BASE}/modules/yukimushi-board-fe`,
    getApi: () => (window as any).YukimushiBoardFe,
  },
  'yukimushi-onechamber-fe': {
    manifest: `${BASE}/modules/yukimushi-onechamber-fe`,
    getApi: () => (window as any).YukimushiOnechamberFe,
  },
  'yukimushi-fi-fe': {
    manifest: `${BASE}/modules/yukimushi-fi-fe`,
    getApi: () => (window as any).YukimushiFiFe,
  },
  // Vite lib 모드(단일 IIFE 번들 + style.css) — React 19로 올린 유일한 모듈이다
  'yukimushi-oneffice-fe': {
    script: `${BASE}/modules/yukimushi-oneffice-fe/module.js`,
    styles: [`${BASE}/modules/yukimushi-oneffice-fe/style.css`],
    getApi: () => (window as any).YukimushiOnefficeFe,
  },
  // Vite lib 모드(단일 IIFE 번들 + style.css) — React 19 (livekit-client를 webpack4가 빌드하지 못해 승격)
  'yukimushi-videoconf-fe': {
    script: `${BASE}/modules/yukimushi-videoconf-fe/module.js`,
    styles: [`${BASE}/modules/yukimushi-videoconf-fe/style.css`],
    getApi: () => (window as any).YukimushiVideoconfFe,
  },
  'yukimushi-basicinfo-fe': {
    script: `${BASE}/modules/yukimushi-basicinfo-fe/module.js`,
    styles: [`${BASE}/modules/yukimushi-basicinfo-fe/style.css`],
    getApi: () => (window as any).YukimushiBasicinfoFe,
  },
};

// 경로 접두사 → moduleCode (이식이 끝난 모듈만 등록. 미등록 경로는 준비 중 화면)
const PATH_TO_MODULE: { prefix: string; code: string }[] = [
  { prefix: '/yukimushi-base-webpack-fe', code: 'yukimushi-base-webpack-fe' },
  { prefix: '/yukimushi-base-vite-fe', code: 'yukimushi-base-vite-fe' },
  { prefix: '/board', code: 'yukimushi-board-fe' },
  { prefix: '/onechamber', code: 'yukimushi-onechamber-fe' },
  { prefix: '/finance', code: 'yukimushi-fi-fe' },
  { prefix: '/oneffice', code: 'yukimushi-oneffice-fe' },
  { prefix: '/videoconf', code: 'yukimushi-videoconf-fe' },
  { prefix: '/basicinfo', code: 'yukimushi-basicinfo-fe' },
  // { prefix: '/mail',  code: 'yukimushi-mail-fe' },    // 3단계에서 등록 예정
];

export function moduleCodeForPath(pathname: string): string | null {
  const hit = PATH_TO_MODULE.find(m => pathname.startsWith(m.prefix));
  return hit ? hit.code : null;
}

const scriptPromises: Record<string, Promise<unknown>> = {};

function loadScript(src: string) {
  if (!scriptPromises[src]) {
    scriptPromises[src] = new Promise((resolve, reject) => {
      const element = document.createElement('script');
      element.src = src;
      element.onload = resolve;
      element.onerror = () => {
        delete scriptPromises[src];
        reject(new Error(`모듈 스크립트 로드 실패: ${src}`));
      };
      document.body.appendChild(element);
    });
  }
  return scriptPromises[src];
}

function loadStyle(href: string) {
  return new Promise((resolve, reject) => {
    const element = document.createElement('link');
    element.rel = 'stylesheet';
    element.href = href;
    element.onload = resolve;
    element.onerror = () => reject(new Error(`모듈 스타일 로드 실패: ${href}`));
    document.head.appendChild(element);
  });
}

// asset-manifest.json의 entrypoints를 순서대로 로드
async function loadManifest(baseUrl: string) {
  const res = await fetch(`${baseUrl}/asset-manifest.json?t=${Date.now()}`);
  const { entrypoints } = (await res.json()) as { entrypoints: string[] };
  for (const entry of entrypoints) {
    const url = `${baseUrl}/${entry}`;
    await (entry.endsWith('.css') ? loadStyle(url) : loadScript(url));
  }
}

async function loadModule(def: ModuleDef) {
  if (def.manifest) return loadManifest(def.manifest);
  // 스타일을 먼저 붙인다 — 스크립트가 곧바로 마운트해도 첫 프레임이 스타일 없이 그려지지 않게
  if (def.styles) await Promise.all(def.styles.map(loadStyle));
  return loadScript(def.script!);
}

/**
 * 모듈 하나를 keep-alive로 유지하는 패널.
 * 처음 active가 될 때 로드+마운트하고, 이후 비활성 시 숨기기만 한다.
 * (호스트 언마운트 시에만 모듈 unmount 호출)
 */
export function ModulePane({ code, active }: { code: string; active: boolean }) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [visited, setVisited] = useState(active);
  const [error, setError] = useState<string | null>(null);
  const moduleDef = MODULES[code];

  useEffect(() => {
    if (active) setVisited(true);
  }, [active]);

  useEffect(() => {
    if (!visited || !moduleDef) return undefined;

    const el = mountRef.current!;
    let cancelled = false;

    loadModule(moduleDef)
      .then(() => {
        if (!cancelled) {
          moduleDef.getApi()!.mount(el);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
        }
      });

    return () => {
      cancelled = true;
      const api = moduleDef.getApi();
      if (api) {
        api.unmount(el);
      }
    };
  }, [visited, moduleDef]);

  if (!visited) return null;

  return (
    <div style={active
      ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }
      : { display: 'none' }
    }>
      {error ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94A3B8' }}>
          <p>{error}</p>
        </div>
      ) : (
        <div ref={mountRef} style={{ flex: 1, minHeight: 0 }} />
      )}
    </div>
  );
}

/** 아직 이식되지 않은 메뉴의 자리 표시 화면 */
export function PlaceholderPane({ label, active }: { label: string; active: boolean }) {
  if (!active) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column', gap: 8,
      alignItems: 'center', justifyContent: 'center', color: '#94A3B8',
    }}>
      <strong style={{ fontSize: 16, color: '#475569' }}>{label}</strong>
      <span>모듈 준비 중입니다.</span>
    </div>
  );
}
